import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";
import { CheckCircle2, FileText, Loader2, Mail, MessageSquare, Monitor, Paperclip, Send, Users } from "lucide-react";

const OPEN = ["open", "negotiating", "rejected"];
const CH_ICON = { system: Monitor, sms: MessageSquare, email: Mail };

/* 把一份批准图纸发给本项目进行中的报价邀请（加入资料包 + 通知已邀请分包；邮件 ≤25MB 真实附件） */
export const SendDrawingDialog = ({ open, onClose, api, zh, projectId, file }) => {
  const [rfqs, setRfqs] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setRfqs(null);
    api.get(`/projects/${projectId}/rfq`).then(({ data }) => {
      const items = (data?.items || []).filter((r) => OPEN.includes(r.status));
      setRfqs(items);
      setSel(Object.fromEntries(items.map((r) => [r.id, true])));
    }).catch(() => setRfqs([]));
  }, [open, api, projectId]);
  const send = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) { toast.error(zh ? "请至少选择一个报价邀请" : "Select at least one RFQ"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/rfq/send-drawing`, { fileId: file.fileId, rfqIds: ids });
      setResult(data);
      const n = data.items.reduce((s, it) => s + (it.sent?.length || 0), 0);
      toast.success(zh ? `已发送给 ${n} 家分包${data.attached ? "（邮件含 PDF 附件）" : ""}` : `Sent to ${n} subs`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
    setBusy(false);
  };
  const inPack = (r) => (r.pack?.drawings || []).some((d) => d.fileId === file?.fileId);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg z-[97]" overlayClassName="z-[96]" data-testid="send-drawing-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Send className="h-4 w-4 text-red-600" />{zh ? "发给分包报价参考" : "Send to subcontractors for quoting"}</DialogTitle>
        </DialogHeader>
        {file && (
          <div className="space-y-3 text-xs">
            <p className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2 text-slate-700" data-testid="send-drawing-file"><FileText className="h-4 w-4 text-red-500 shrink-0" /><span className="truncate flex-1">{file.filename}</span>{file.pageCount ? <span className="text-slate-400 shrink-0">PDF · {file.pageCount} {zh ? "页" : "p"}</span> : null}</p>
            <p className="text-slate-500">{zh ? "图纸将加入所选报价邀请的资料包，并通知全部已邀请分包（系统铃铛 / 短信附链接 / 邮件，PDF ≤ 25MB 作为邮件附件）。" : "The drawing is added to the RFQ pack and all invited subs are notified (bell / SMS link / email with PDF attachment ≤ 25MB)."}</p>
            {rfqs == null ? <p className="text-slate-400 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{zh ? "加载报价邀请…" : "Loading…"}</p>
              : rfqs.length === 0 ? <p className="text-slate-400" data-testid="send-drawing-empty">{zh ? "本项目暂无进行中的报价邀请。请先在任务链的 RFQ 卡发起邀请。" : "No open RFQs for this project."}</p>
              : (
                <div className="space-y-1.5" data-testid="send-drawing-rfqs">
                  {rfqs.map((r) => {
                    const live = (r.invites || []).filter((i) => i.status !== "declined").length;
                    return (
                      <label key={r.id} data-testid={`send-drawing-rfq-${r.id}`} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 cursor-pointer ${sel[r.id] ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}>
                        <input type="checkbox" checked={!!sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.checked })} data-testid={`send-drawing-check-${r.id}`} />
                        <span className="font-semibold text-slate-800">{r.tradeName}</span>
                        <span className="text-slate-500 inline-flex items-center gap-1"><Users className="h-3 w-3" />{live} {zh ? "家已邀请" : "invited"}</span>
                        <span className="text-slate-400">{r.statusZh}</span>
                        {inPack(r) && <span className="ml-auto text-emerald-700 inline-flex items-center gap-0.5" data-testid={`send-drawing-inpack-${r.id}`}><CheckCircle2 className="h-3 w-3" />{zh ? "已在资料包" : "in pack"}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            {result && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 space-y-1" data-testid="send-drawing-result">
                <p className="font-semibold text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />{zh ? "已发送" : "Sent"}{result.attached && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Paperclip className="h-3 w-3" />{zh ? "邮件含附件" : "attached"}</span>}</p>
                {result.items.map((it) => (
                  <div key={it.rfqId} className="text-[11px] text-slate-700">
                    <b>{it.tradeName || it.rfqId}</b>{it.skipped ? ` · ${it.skipped}` : ""}
                    {(it.sent || []).map((s) => <span key={s.inviteId} className="ml-2 inline-flex items-center gap-0.5">{s.companyName}{s.channels.length ? s.channels.map((c) => { const I = CH_ICON[c] || Monitor; return <I key={c} className="h-3 w-3 text-emerald-600" />; }) : <span className="text-slate-400">（无可用渠道）</span>}</span>)}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} data-testid="send-drawing-close">{zh ? "关闭" : "Close"}</Button>
              {!result && <Button size="sm" onClick={send} disabled={busy || !rfqs?.length} className="bg-brand-500 hover:bg-brand-600" data-testid="send-drawing-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}{zh ? "发送" : "Send"}</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
